import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getFavorites,
  removeFavorite,
  getLocalOrders,
  setLocalOrders,
  getLastMarket,
  type LocalOrderInfo,
  type LastMarketInfo,
} from '@paulownia/shared/lib/storage';
import { onOrderSnapshot, getOrderById } from '@paulownia/shared/services/orders';
import { getTenantBySlug } from '@paulownia/shared/services/tenant';
import { getMarketById, getMarketTenantsWithDetails } from '@paulownia/shared/services/markets';
import { initCustomerAuth } from '@/lib/customer-auth';
import { useMerchantCarts } from '@/contexts/CartContext';
import type { OrderStatus, DayOfWeek } from '@paulownia/shared/types';
import { DAY_OF_WEEK_LABELS } from '@paulownia/shared/types';
import { Store, Heart, Package, MapPin, ShoppingBag } from 'lucide-react';
import { Skeleton } from '@paulownia/shared';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import { PullToRefresh } from '@/components/PullToRefresh';
import { toast } from 'sonner';
import { reportError } from '@paulownia/shared/lib/error-reporting';
import {
  OrderStatusCard,
  MerchantCard,
  MarketSection,
  OrderDetailModal,
  TodayMarketCard,
  MarketSearchSection,
} from './components';

interface OrderWithStatus extends LocalOrderInfo {
  status: OrderStatus;
  pickupTime?: string;
  isLoading?: boolean;
  recurringOrderId?: string;
}

interface FavoriteWithName {
  slug: string;
  name: string;
}

interface SelectedOrder {
  orderId: string;
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  pickupTime?: string;
}

interface TodayMarketInfo {
  marketId: string;
  marketCity: string;
  dayOfWeek: DayOfWeek;
  openingTime: string;
  closingTime: string;
  merchantCount: number;
  closingSoon: boolean;
}

// Check if a date is today
function isToday(dateString: string): boolean {
  const date = new Date(dateString);
  const today = new Date();
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
}

// Check if market is closing soon (within 30 minutes)
function isClosingSoon(closingTime: string): boolean {
  const now = new Date();
  const [hours, minutes] = closingTime.split(':').map(Number);
  const closingDate = new Date();
  closingDate.setHours(hours, minutes, 0, 0);

  const diffMs = closingDate.getTime() - now.getTime();
  const diffMinutes = diffMs / (1000 * 60);

  return diffMinutes > 0 && diffMinutes <= 30;
}

export function MerchantPortal() {
  const navigate = useNavigate();
  const { carts } = useMerchantCarts();
  const [favorites, setFavorites] = useState<FavoriteWithName[]>([]);
  const [orders, setOrders] = useState<OrderWithStatus[]>([]);
  const [lastMarket, setLastMarket] = useState<LastMarketInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<SelectedOrder | null>(null);

  // Today's market state
  const [todayMarket, setTodayMarket] = useState<TodayMarketInfo | null>(null);

  // Store unsubscribe functions for real-time listeners
  const unsubscribeFnsRef = useRef<Map<string, () => void>>(new Map());
  // Store local orders for subscription management
  const localOrdersRef = useRef<LocalOrderInfo[]>([]);

  // Filter orders to today only
  const todayOrders = useMemo(() => {
    return orders.filter(o => !o.isLoading && isToday(o.createdAt));
  }, [orders]);

  // Callback to update a single order's status
  const updateOrderStatus = useCallback((orderId: string, status: OrderStatus | null, pickupTime?: string, recurringOrderId?: string) => {
    setOrders(prevOrders => {
      if (status === null) {
        // Order was deleted, remove it from the list
        return prevOrders.filter(o => o.orderId !== orderId);
      }

      // Filter out completed orders older than 24h
      const now = new Date();
      const existingOrder = prevOrders.find(o => o.orderId === orderId);
      if (existingOrder) {
        if (status === 'done') {
          const createdAt = new Date(existingOrder.createdAt);
          const hoursDiff = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);
          if (hoursDiff >= 24) {
            return prevOrders.filter(o => o.orderId !== orderId);
          }
        }
        return prevOrders.map(o =>
          o.orderId === orderId
            ? { ...o, status, pickupTime: pickupTime || o.pickupTime, isLoading: false, recurringOrderId: recurringOrderId || o.recurringOrderId }
            : o
        );
      }
      return prevOrders;
    });
  }, []);

  // Setup real-time subscriptions for orders
  const setupOrderSubscriptions = useCallback((localOrders: LocalOrderInfo[]): Promise<void> => {
    // Cleanup existing subscriptions
    unsubscribeFnsRef.current.forEach(unsub => unsub());
    unsubscribeFnsRef.current.clear();

    const recentOrders = localOrders.slice(0, 5);

    // If no orders, resolve immediately
    if (recentOrders.length === 0) {
      setOrders([]);
      return Promise.resolve();
    }

    // Initialize orders with loading state
    const initialOrders: OrderWithStatus[] = recentOrders.map(order => ({
      ...order,
      status: 'pending' as OrderStatus,
      pickupTime: undefined,
      isLoading: true,
    }));
    setOrders(initialOrders);

    // Track which orders have received their first snapshot
    const pendingOrders = new Set(recentOrders.map(o => o.orderId));

    return new Promise((resolve) => {
      for (const order of recentOrders) {
        let isFirstSnapshot = true;

        const unsubscribe = onOrderSnapshot(
          order.orderId,
          (orderData) => {
            if (orderData) {
              const pickupDate = new Date(orderData.pickupAtISO);
              const pickupTime = pickupDate.toLocaleTimeString('fr-FR', {
                hour: '2-digit',
                minute: '2-digit',
              });
              updateOrderStatus(order.orderId, orderData.status, pickupTime, orderData.recurringOrderId);
            } else {
              updateOrderStatus(order.orderId, null);
            }

            if (isFirstSnapshot) {
              isFirstSnapshot = false;
              pendingOrders.delete(order.orderId);
              if (pendingOrders.size === 0) {
                resolve();
              }
            }
          },
          (error: { code?: string; message?: string }) => {
            console.error('[MerchantPortal] Order subscription error:', error.code, error.message);
            if (error.code === 'permission-denied') {
              updateOrderStatus(order.orderId, null);
            }

            if (isFirstSnapshot) {
              isFirstSnapshot = false;
              pendingOrders.delete(order.orderId);
              if (pendingOrders.size === 0) {
                resolve();
              }
            }
          }
        );
        unsubscribeFnsRef.current.set(order.orderId, unsubscribe);
      }

      // Safety timeout
      setTimeout(() => {
        if (pendingOrders.size > 0) {
          console.warn('[MerchantPortal] Timeout waiting for order snapshots');
          resolve();
        }
      }, 5000);
    });
  }, [updateOrderStatus]);

  // Load today's market info based on today's orders
  const loadTodayMarketInfo = useCallback(async (todayLocalOrders: LocalOrderInfo[]) => {
    // Find today's orders that have a marketId
    const ordersWithMarket = todayLocalOrders.filter(o => o.marketId && isToday(o.createdAt));

    if (ordersWithMarket.length === 0) {
      setTodayMarket(null);
      return;
    }

    // Get the first market (assuming all today's orders are on the same market)
    const marketId = ordersWithMarket[0].marketId!;

    try {
      const [market, tenants] = await Promise.all([
        getMarketById(marketId),
        getMarketTenantsWithDetails(marketId),
      ]);

      if (!market) {
        setTodayMarket(null);
        return;
      }

      setTodayMarket({
        marketId,
        marketCity: market.city,
        dayOfWeek: market.dayOfWeek,
        openingTime: market.openingTime,
        closingTime: market.closingTime,
        merchantCount: tenants.length,
        closingSoon: isClosingSoon(market.closingTime),
      });
    } catch (error) {
      console.error('Error loading today market info:', error);
      setTodayMarket(null);
    }
  }, []);

  useEffect(() => {
    loadData();

    const unsubscribeFns = unsubscribeFnsRef.current;

    return () => {
      unsubscribeFns.forEach(unsub => unsub());
      unsubscribeFns.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadData() {
    setIsLoading(true);
    try {
      await initCustomerAuth();

      const [favSlugs, localOrders, market] = await Promise.all([
        getFavorites(),
        getLocalOrders(),
        getLastMarket(),
      ]);

      // Clean up stale local orders that no longer exist in Firestore
      const recentLocalOrders = localOrders.slice(0, 5);
      const orderChecks = await Promise.all(
        recentLocalOrders.map(async (order) => {
          try {
            const exists = await getOrderById(order.orderId);
            return { order, exists: !!exists };
          } catch {
            return { order, exists: false };
          }
        })
      );

      const validOrders = orderChecks
        .filter(({ exists }) => exists)
        .map(({ order }) => order);

      const uncheckedOrders = localOrders.slice(5);
      const cleanedOrders = [...validOrders, ...uncheckedOrders];

      if (cleanedOrders.length < localOrders.length) {
        console.log('[MerchantPortal] Cleaned up stale orders:', localOrders.length - cleanedOrders.length);
        await setLocalOrders(cleanedOrders);
      }

      // Load tenant names for favorites
      const favsWithNames = await Promise.all(
        favSlugs.map(async (s) => {
          try {
            const tenant = await getTenantBySlug(s);
            return { slug: s, name: tenant?.name || s };
          } catch {
            return { slug: s, name: s };
          }
        })
      );
      setFavorites(favsWithNames);

      localOrdersRef.current = cleanedOrders;

      // Setup real-time subscriptions for valid orders only
      setupOrderSubscriptions(validOrders);

      // Load today's market info
      const todayLocalOrders = cleanedOrders.filter(o => isToday(o.createdAt));
      await loadTodayMarketInfo(todayLocalOrders);

      setLastMarket(market);
    } catch (error) {
      reportError(error, { context: 'MerchantPortal.loadData' });
    } finally {
      setIsLoading(false);
    }
  }


  function navigateToMerchant(merchantSlug: string) {
    navigate(`/${merchantSlug}`);
  }

  function handleOrderClick(order: OrderWithStatus) {
    setSelectedOrder({
      orderId: order.orderId,
      tenantId: order.tenantId,
      tenantName: order.tenantName || order.tenantSlug,
      tenantSlug: order.tenantSlug,
      pickupTime: order.pickupTime,
    });
  }

  function handleCloseOrderModal() {
    setSelectedOrder(null);
  }


  async function handleRemoveFavorite(merchantSlug: string) {
    try {
      await removeFavorite(merchantSlug);
      await loadData();
    } catch (error) {
      reportError(error, { context: 'MerchantPortal.handleRemoveFavorite', merchantSlug });
    }
  }

  // Pull-to-refresh handler - loadData is intentionally not in deps (stable function reference)
  const handleRefresh = useCallback(async () => {
    try {
      await loadData();
      toast.success('Données actualisées');
    } catch (error) {
      console.error('[MerchantPortal] Pull-to-refresh error:', error);
      toast.error('Impossible de rafraîchir');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pull-to-refresh hook
  const {
    containerRef: pullToRefreshRef,
    pullDistance,
    isRefreshing,
    isPulling,
  } = usePullToRefresh({
    onRefresh: handleRefresh,
    isEnabled: !isLoading,
  });


  // Check if we have today's orders (post-order state)
  const hasOrdersToday = todayOrders.length > 0;

  return (
    <PullToRefresh
      ref={pullToRefreshRef}
      pullDistance={pullDistance}
      isRefreshing={isRefreshing}
      isPulling={isPulling}
      className="h-full bg-gradient-to-b from-orange-50 to-white"
    >
      {/* Header */}
      <div className="bg-orange-600 text-white p-6 pb-8">
        <div className="max-w-2xl mx-auto text-center">
          <div className="flex justify-center mb-3">
            <Store className="w-12 h-12" />
          </div>
          <h1 className="text-3xl font-bold">Paulownia</h1>
          <p className="text-orange-100 mt-1">Commandez chez vos commercants</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 -mt-4 pb-8 space-y-6">
        {/* Loading Skeletons */}
        {isLoading && (
          <>
            {/* Orders Skeleton */}
            <div className="bg-white rounded-xl shadow-md p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Skeleton className="w-5 h-5 rounded" />
                <Skeleton className="h-5 w-32" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-16 w-full rounded-lg" />
                <Skeleton className="h-16 w-full rounded-lg" />
              </div>
            </div>

            {/* Market Card Skeleton */}
            <Skeleton className="h-48 w-full rounded-2xl" />

            {/* Favorites Skeleton */}
            <div className="bg-white rounded-xl shadow-md p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Skeleton className="w-5 h-5 rounded" />
                <Skeleton className="h-5 w-24" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-12 w-full rounded-lg" />
              </div>
            </div>
          </>
        )}

        {/* BLOC 1: Today's Orders Section (Priority - always at top when orders exist) */}
        {!isLoading && hasOrdersToday && (
          <div className="bg-white rounded-xl shadow-md p-4 space-y-3">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Package className="w-5 h-5 text-orange-600" />
              Vos commandes aujourd'hui
            </h2>
            <div className="space-y-2">
              {todayOrders.map((order) => (
                <OrderStatusCard
                  key={order.orderId}
                  orderId={order.orderId}
                  tenantName={order.tenantName || order.tenantSlug}
                  tenantSlug={order.tenantSlug}
                  status={order.status}
                  pickupTime={order.pickupTime}
                  isRecurring={!!order.recurringOrderId}
                  onClick={() => handleOrderClick(order)}
                />
              ))}
            </div>
          </div>
        )}

        {/* BLOC 2: Today's Market Card (Key UX - prominent after order) */}
        {!isLoading && hasOrdersToday && todayMarket && (
          <TodayMarketCard
            marketId={todayMarket.marketId}
            marketCity={todayMarket.marketCity}
            marketDayOfWeek={todayMarket.dayOfWeek}
            openingTime={todayMarket.openingTime}
            closingTime={todayMarket.closingTime}
            merchantCount={todayMarket.merchantCount}
            closingSoon={todayMarket.closingSoon}
          />
        )}

        {/* Mes paniers - active carts across merchants */}
        {!isLoading && carts.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-4 space-y-3">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-orange-600" />
              Mes paniers
            </h2>
            <div className="space-y-2">
              {carts.map((cart) => (
                <button
                  key={cart.merchantId}
                  onClick={() => navigate(`/${cart.merchantSlug}`)}
                  className="w-full bg-gray-50 rounded-lg p-3 text-left hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">{cart.merchantName}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3" />
                        {cart.marketCity} — {DAY_OF_WEEK_LABELS[cart.marketDayOfWeek as DayOfWeek]}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-orange-600">
                        {cart.items.length} article{cart.items.length > 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Previous Orders (not today) */}
        {!isLoading && orders.filter(o => !o.isLoading && !isToday(o.createdAt)).length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-4 space-y-3">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Package className="w-5 h-5 text-gray-500" />
              Commandes précédentes
            </h2>
            <div className="space-y-2">
              {orders.filter(o => !o.isLoading && !isToday(o.createdAt)).map((order) => (
                <OrderStatusCard
                  key={order.orderId}
                  orderId={order.orderId}
                  tenantName={order.tenantName || order.tenantSlug}
                  tenantSlug={order.tenantSlug}
                  status={order.status}
                  pickupTime={order.pickupTime}
                  isRecurring={!!order.recurringOrderId}
                  onClick={() => handleOrderClick(order)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Last Market Section (only when no today's market card) */}
        {!isLoading && !hasOrdersToday && lastMarket && (
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-orange-600 flex-shrink-0" />
                Mon marché
              </h2>
            </div>
            <MarketSection
              marketId={lastMarket.marketId}
              marketCity={lastMarket.marketCity}
            />
          </div>
        )}

        {/* Favorites Section */}
        {!isLoading && favorites.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-4 space-y-3">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-500 fill-red-500" />
              Mes favoris
            </h2>
            <div className="space-y-2">
              {favorites.map((favorite) => (
                <MerchantCard
                  key={favorite.slug}
                  name={favorite.name}
                  slug={favorite.slug}
                  onNavigate={navigateToMerchant}
                  onRemove={handleRemoveFavorite}
                />
              ))}
            </div>
          </div>
        )}

        {/* Market Search Section */}
        {!isLoading && <MarketSearchSection />}

        {/* Empty State */}
        {!isLoading && orders.length === 0 && favorites.length === 0 && !lastMarket && (
          <div className="text-center py-8 text-gray-500">
            <Store className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium text-gray-600">Bienvenue sur Paulownia</p>
            <p className="text-sm mt-2">
              Sélectionnez un marché pour découvrir les commerçants
            </p>
          </div>
        )}
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <OrderDetailModal
          orderId={selectedOrder.orderId}
          tenantId={selectedOrder.tenantId}
          tenantName={selectedOrder.tenantName}
          tenantSlug={selectedOrder.tenantSlug}
          pickupTime={selectedOrder.pickupTime}
          isOpen={true}
          onClose={handleCloseOrderModal}
          onNavigateToMerchant={navigateToMerchant}
        />
      )}

    </PullToRefresh>
  );
}
